/**
 * @file CourseSelection.jsx
 * @author Rishikesh
 * @date 2024-09-09
 * @description Component of users to manage courses
 */
import React from "react";
import {
  Box,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
  InputAdornment,
  Skeleton,
} from "@mui/material";
import { ContentPasteSearchOutlined } from "@mui/icons-material";

export const CourseSelection = ({
  isLoading,
  selectedCountry,
  courses,
  courseCountry,
  setCourseCountry,
}) => {
  const [searchQuery, setSearchQuery] = React.useState("");

  const filteredCourses = courses?.filter((item) =>
    item.coursename.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const isChecked = (courseId) => {
    const countryData = courseCountry.find(
      (c) => c.countryid === selectedCountry?.id
    );
    return countryData
      ? countryData.courses.includes(courseId.courseid)
      : false;
  };

  const handleToggleCourse = (courseId) => () => {
    const selectedCountryId = selectedCountry.id;

    const existingCountryIndex = courseCountry.findIndex(
      (item) => item.countryid === selectedCountryId
    );

    if (existingCountryIndex !== -1) {
      const updatedCourses = [...courseCountry[existingCountryIndex].courses];

      const courseIndex = updatedCourses.findIndex(
        (course) => course === courseId
      );

      if (courseIndex !== -1) {
        updatedCourses.splice(courseIndex, 1);
      } else {
        updatedCourses.push(courseId);
      }

      const updatedCourseCountry = [...courseCountry];
      updatedCourseCountry[existingCountryIndex].courses = updatedCourses;

      // If no courses left, remove the country
      // if (updatedCourses.length === 0) {
      //   updatedCourseCountry.splice(existingCountryIndex, 1);
      // }

      setCourseCountry(updatedCourseCountry);
    }
  };

  return (
    <Box
      flex={1}
      borderRadius="12px"
      px="8px"
      py={3}
      bgcolor="#F7F7F7"
      display="flex"
      flexDirection="column"
    >
      <Stack
        pb={1}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
      >
        {isLoading ? (
          <Skeleton variant="rounded" width={140} height={24} />
        ) : (
          <Typography fontWeight={400} fontSize={18} color="#222222">
            Courses
          </Typography>
        )}
      </Stack>
      <Box>
        {isLoading ? (
          <Skeleton variant="rounded" width={180} height={18} />
        ) : (
          <Typography color="textDisabled">
            Choose courses that you want to assign to this lead
          </Typography>
        )}

        {isLoading ? (
          <Skeleton
            variant="rounded"
            sx={{ mt: 3, mb: 1 }}
            width={"100%"}
            height={40}
          />
        ) : (
          <TextField
            id="outlined-start-adornment"
            placeholder="Search course"
            fullWidth
            className="inputMdRounded"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ bgcolor: "transparent" }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <ContentPasteSearchOutlined />
                  </InputAdornment>
                ),
              },
            }}
          />
        )}
      </Box>
      {selectedCountry ? (
        <List dense sx={{ width: "100%", flex: 1, overflowY: "auto" }}>
          {isLoading ? (
            Array.from({ length: 7 }).map((_, index) => (
              <Skeleton
                key={index}
                variant="rounded"
                sx={{ mt: 1 }}
                width={"100%"}
                height={32}
              />
            ))
          ) : filteredCourses && filteredCourses.length > 0 ? (
            filteredCourses?.map((item) => (
              <ListItem
                key={item.courseid}
                secondaryAction={
                  <Checkbox
                    edge="end"
                    onChange={handleToggleCourse(item.courseid)}
                    checked={isChecked(item)}
                    sx={{
                      display: isChecked(item) ? "inline-flex" : "none",
                    }}
                  />
                }
                disablePadding
                sx={{
                  bgcolor: isChecked(item) ? "#E4EBF4" : "transparent",
                  "&:hover": {
                    "& .MuiCheckbox-root": {
                      display: "inline-flex",
                    },
                  },
                }}
              >
                <ListItemButton>
                  <ListItemText primary={item.coursename} />
                </ListItemButton>
              </ListItem>
            ))
          ) : (
            <Typography
              sx={{ mt: 2, textAlign: "center", color: "text.secondary" }}
            >
              No courses available.
            </Typography>
          )}
        </List>
      ) : (
        <Box
          height="100%"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          {isLoading ? (
            <Skeleton variant="rounded" width="60%" height={24} />
          ) : (
            <Typography textAlign="center">Please choose a country</Typography>
          )}
        </Box>
      )}
    </Box>
  );
};
